/**
 * Created by neo on 03.04.23.
 */
import { makeAutoObservable } from 'mobx';
import { Pageable } from '../Pageable';
import { Audited, createAuditedFields } from '../Audited';
import { HttpBackend } from '../../Services/Http/HttpBackend';

export type CustomerGroupQueryRequest = Pageable & {
  gymId: string;
};

// Observable model type
export type CustomerGroup = Audited & {
  gymId: string;
  name: string;
  description: string;
  save(): Promise<CustomerGroup>;
  delete(): Promise<CustomerGroup>;
};

// Factory function
export function createCustomerGroup(json?: Partial<CustomerGroup>): CustomerGroup {
  const audited = createAuditedFields(json);

  return makeAutoObservable({
    ...audited,
    gymId: json?.gymId ?? '',
    name: json?.name ?? '',
    description: json?.description ?? '',

    save(): Promise<CustomerGroup> {
      return HttpBackend.post(`/gym/customer/group/admin?gymId=${this.gymId}`, this).then(() => this);
    },

    delete(): Promise<CustomerGroup> {
      return HttpBackend.delete(`/gym/customer/group/admin?gymId=${this.gymId}`, this).then(() => this);
    },
  });
}

// Static API methods
export const CustomerGroupApi = {
  findOne(groupId: string, gymId: string): Promise<CustomerGroup | undefined> {
    return HttpBackend.get(`/gym/customer/group/${groupId}?gymId=${gymId}`, groupId).then((res) =>
      res ? createCustomerGroup(res) : undefined,
    );
  },

  find(request?: Partial<CustomerGroupQueryRequest>): Promise<CustomerGroup[]> {
    return HttpBackend.get(`/gym/customer/group/admin`, request).then(
      (res) => res?.map((r) => createCustomerGroup(r)) ?? [],
    );
  },

  count(request?: Partial<CustomerGroupQueryRequest>): Promise<number> {
    return HttpBackend.get(`/gym/customer/group/admin/count`, request).then((res) => res ?? 0);
  },

  list(): Promise<CustomerGroup[]> {
    return HttpBackend.get(`/gym/customer/group/me`).then((res) => res?.map((r) => createCustomerGroup(r)) ?? []);
  },

  current(): Promise<CustomerGroup | undefined> {
    return HttpBackend.get(`/gym/customer/group/me/current`).then((res) =>
      res ? createCustomerGroup(res) : undefined,
    );
  },

  setCurrent(groupId: string): Promise<CustomerGroup> {
    return HttpBackend.post(`/gym/customer/group/me/current?groupId=${groupId}`).then((res) =>
      createCustomerGroup(res),
    );
  },
};
